import { formatDay, getLang, t } from '../i18n'
import { BASE_BRAND_ID } from '../brand/baseBrand'
import { SERVICE_URL, type PlateMark } from '../maps/legendRows'
import { slugify } from './translit'

/**
 * Плашка на скачанной карте: что за кадр и при какой глубине он справедлив.
 *
 * Владелец 2026-08-07: «при скачивании карты в PNG или GIF оставить в углу
 * легенду относительно глубины, для которой данная карта справедлива и
 * актуальна, — по сути унаследовать её из ползунка глубины в сайдбаре».
 *
 * Рисуем плашку сами в canvas, а не снимаем DOM: библиотеки скриншота DOM тянут
 * внешние ресурсы, а у витрин строгий CSP. Здесь — чистая часть (текст, размеры,
 * имя файла), она и тестируется; сама отрисовка — в `exportImage.ts`.
 */

/**
 * Бренд домена в том виде, в каком он нужен скачиваемому файлу.
 *
 * Оба поля — ДАННЫЕ из бренд-пака (`/api/v1/cabinet/site`), а не подписи из
 * каталога переводов. Разница видна только на партнёрском домене, и видна она
 * снаружи: файл клиент рассылает дальше, и «rumb_…» в руках клиента партнёра
 * называл бы чужой сервис. Каталог остаётся запасным путём для имени (пак не
 * доехал) — этим ведает `useBrandName`.
 *
 * На саму КАРТИНКУ имя бренда с 02.09.2026 не попадает (владелец): им зовётся
 * файл и подписывается `parametry.txt` в архиве серии, а плашка называет слой.
 */
export interface ExportBrand {
  /** Код пака: он же префикс имени файла. Латиница и дефисы. */
  id: string
  /** Имя бренда на языке кабинета. На плашке кадра его НЕТ — только в
   *  `parametry.txt` архива серии. */
  name: string
}

export interface ExportContext {
  /** Бренд домена: чьим кодом назван файл и чьё имя стоит в `parametry.txt`. */
  brand: ExportBrand
  /** Название слоя/модели, как в сайдбаре. */
  layerLabel: string
  /** Дата съёмки, YYYY-MM-DD. */
  date: string
  depth: number
  /** Применима ли глубина к слою (батиметрия/луна — нет). */
  depthApplicable: boolean
  /** Вид, если выбран. */
  speciesLabel: string | null
  /** Строки легенды активных наложений — те же, что в плашке на карте. */
  legend?: PlateLine[]
  /** Подпись последней строкой: задаёт владелец в админке. Пустая строка —
   *  «не выводить ничего», `undefined` — настройка ещё не дошла. */
  caption?: string
}

/** Строка плашки: текст и, если нужно, цветная метка слева. */
export interface PlateLine {
  text: string
  mark?: PlateMark
}

/**
 * Потолок длины строки плашки для СВЯЗНОГО ТЕКСТА.
 *
 * Плашка меряет свою ширину по самой длинной строке (`plateBox`), и до
 * 07.09.2026 все её строки были подписями в два-три слова. Смысловое пояснение
 * апвеллинга — два предложения; положенное одной строкой, оно растянуло бы
 * плашку через весь кадр. Число подобрано под ширину прочих строк плашки.
 */
export const PLATE_PROSE_CHARS = 46

/**
 * Разбить связный текст на строки плашки по словам.
 *
 * Слово длиннее строки НЕ режется: разрезанное посреди, оно перестаёт быть
 * словом, а плашка от одного длинного термина всего лишь станет шире — цена
 * несопоставима.
 */
export function wrapPlateText(text: string, max = PLATE_PROSE_CHARS): string[] {
  const words = text.trim().split(/\s+/).filter(Boolean)
  if (!words.length) return []
  const lines: string[] = []
  let line = ''
  for (const w of words) {
    if (!line) line = w
    else if (line.length + 1 + w.length <= max) line += ` ${w}`
    else { lines.push(line); line = w }
  }
  lines.push(line)
  return lines
}

/** Поля плашки, px (в координатах готового кадра). */
export const PLATE_PAD = 10
export const PLATE_LINE_H = 16

/** Строки плашки сверху вниз. Первая — подпись сервиса: картинка уходит наружу. */
export function exportPlateLines(ctx: ExportContext): PlateLine[] {
  const lines: PlateLine[] = []
  // Подпись НАВЕРХУ (владелец 2026-08-08). Строкой снизу она читалась последней,
  // если вообще читалась: пересланную картинку смотрят мельком, и «откуда это и
  // где живая карта» должно попадаться на глаза первым, до разбора слоёв.
  //
  // Текст задаёт владелец в админке. Пустая строка — «не выводить ничего»;
  // `undefined` (настройка ещё не дошла) ≠ пустой строке: молча снять подпись с
  // картинки, которая уходит наружу, из-за неотвеченного запроса нельзя.
  const caption = ctx.caption === undefined ? SERVICE_URL : ctx.caption.trim()
  if (caption) lines.push({ text: caption })
  // Заголовком стоит СЛОЙ, и только он (владелец 2026-09-02). Раньше строка
  // была «<бренд> · <слой>», и самая заметная строка плашки наполовину состояла
  // из слова, которое про кадр не говорит ничего: чей это сервис, уже сказано
  // адресом строкой выше, а на партнёрском домене — его адресом.
  //
  // Имя бренда при этом ОСТАЁТСЯ в `ExportContext`: им зовётся файл
  // (`exportFileName`) и подписывается `parametry.txt` в архиве серии. Убрано
  // оно с картинки, а не из выгрузки.
  lines.push(
    { text: ctx.layerLabel },
    { text: formatDay(getLang(), ctx.date) },
  )
  // Глубина — то, ради чего плашка и заведена. Слою без глубины пишем это
  // словами: «0 м» у батиметрии читалось бы как поверхность.
  lines.push({
    text: ctx.depthApplicable
      ? t('map.export.depth', { depth: Math.round(ctx.depth) })
      : t('map.export.noDepth'),
  })
  if (ctx.speciesLabel) lines.push({ text: ctx.speciesLabel })
  // Легенда наложений уезжает вместе с картинкой: линии и точки на скачанном
  // PNG иначе нечем объяснить (правка владельца 2026-08-07).
  if (ctx.legend?.length) lines.push(...ctx.legend)
  return lines
}

/** Сколько верхних строк плашки составляют шапку: подпись и название слоя. */
export const PLATE_HEADER_LINES = 2

/** Шапка ужимается до одной строки, когда подпись снята: иначе ярко подсветилась
 *  бы дата, оказавшаяся на её месте. */
export function plateHeaderLines(ctx: ExportContext): number {
  const captionShown = ctx.caption === undefined || ctx.caption.trim() !== ''
  return captionShown ? PLATE_HEADER_LINES : PLATE_HEADER_LINES - 1
}

/** Цвет строки плашки по её номеру. Шапка ярче остального — раньше ярче
 *  рисовалась ровно первая строка, потому что ею был бренд; подняв наверх
 *  адрес, вторую строку шапки нельзя было уронить в общую приглушённую массу. */
export function plateLineColor(index: number, headerLines = PLATE_HEADER_LINES): string {
  return index < headerLines ? '#ffffff' : 'rgba(226,238,250,0.86)'
}

/** Габариты плашки по ИЗМЕРЕННОЙ ширине самой длинной строки.
 *
 *  Меряет вызывающий (`ctx.measureText`), а не считает по «средней ширине
 *  символа»: кириллица в моноширинном шрифте оказалась шире оценки, и текст
 *  упирался в край плашки. */
export function plateBox(maxTextWidth: number, lineCount: number) {
  return {
    width: Math.ceil(maxTextWidth) + PLATE_PAD * 2,
    height: lineCount * PLATE_LINE_H + PLATE_PAD * 2,
  }
}

/** То же по оценке ширины символа — запасной путь, когда мерить нечем. */
export function plateSize(lines: PlateLine[], charW: number): { width: number; height: number } {
  const longest = lines.reduce((m, l) => Math.max(m, l.text.length), 0)
  return plateBox(longest * charW, lines.length)
}

/** Название вида → кусок имени файла (таблица транслитерации и объяснение,
 *  почему не английское имя из справочника, — в `map/translit.ts`). */
export function speciesSlug(label: string | null | undefined): string {
  return slugify(label)
}

/** Имя скачиваемого файла: сервис, вид, дата, глубина. Бренд остался ЗДЕСЬ и
 *  в `parametry.txt` — с плашки кадра он снят (владелец 2026-09-02).
 *
 *  Вид добавлен по просьбе владельца 09.08.2026: карты разных видов за одну
 *  дату складываются в одну папку, и без вида отличить их нечем — имя выходило
 *  одинаковым, а второй файл браузер дописывал как «(1)».
 *
 *  Первым идёт КОД БРЕНДА домена, а не зашитое «rumb»: файл ложится в папку
 *  получателя и живёт там своим именем — на домене партнёра «rumb_…» называл бы
 *  чужой сервис. Код прогоняется через тот же `slugify`, что и вид: схема
 *  ограничивает `brand_id` латиницей (миграция 284), но имя файла обязано быть
 *  безопасным независимо от того, что ответила ручка.
 */
export function exportFileName(
  ctx: ExportContext,
  ext: 'png' | 'mp4' | 'zip',
  /** Пометка вида работы («series»): архив серии и одиночный кадр за ту же дату
   *  иначе выглядели бы одним файлом в двух форматах. */
  token?: string,
): string {
  const parts = [slugify(ctx.brand.id) || BASE_BRAND_ID]
  const species = speciesSlug(ctx.speciesLabel)
  // Вид идёт сразу за брендом: в списке файлов сортировка по имени соберёт
  // карты одного вида рядом, а это и есть привычный способ их искать.
  if (species) parts.push(species)
  parts.push(ctx.date)
  if (ctx.depthApplicable) parts.push(`${Math.round(ctx.depth)}m`)
  if (token) parts.push(slugify(token))
  return `${parts.join('_')}.${ext}`.toLowerCase()
}
